import React, { useState, useRef, useEffect } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    VStack,
    HStack,
    Flex,
    Box,
    Text,
    Badge,
    Icon,
    Avatar,
    Divider,
    Button,
    Image,
    Textarea,
    FormControl,
    FormLabel,
    Input,
    Select,
    useToast,
} from "@chakra-ui/react";
import { MdUploadFile, MdClose, MdCalendarToday, MdPhone, MdEmail, MdWarning } from "react-icons/md";
import axios from "axios";
import { BASE_URL } from "config/api";
import LoadingSpinner from "components/LoadingSpinner";
import MissionSearchInput from "components/MissionSearchInput";
import starIcon from "assets/img/icons/Avis/star.png";
import starEmptyIcon from "assets/img/icons/Avis/star-empty.png";

const TYPES_RECLAMATION = ["Delai", "Erreur medicale", "Communication", "Materiel", "Autre"];
const NIVEAUX_GRAVITE   = ["Faible", "Moyen", "Critique"];
const SERVICES          = ["Ambulancier", "Infirmier", "Regulateur", "Medecin"];
const MAX_FILE_SIZE     = 5 * 1024 * 1024; // 5 Mo
const MAX_FILES         = 5;
const graviteColor      = { Critique: "red", Moyen: "orange", Faible: "green" };

const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const TraitementReclamationModal = ({
    isOpen,
    onClose,
    viewData,
    loadingDetails,
    missionData,
    patientData,
    onCreated,
}) => {
    const toast      = useToast();
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
        type_reclamation : "",
        niveau_gravite   : "",
        service_concerne : "",
        description      : "",
        reference_dossier: "",
    });
    const [selectedMissionId, setSelectedMissionId] = useState(null);
    const [defaultMission, setDefaultMission]       = useState(null);
    const [fichiers, setFichiers]     = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Pré-remplir reference_dossier lorsque viewData change
    useEffect(() => {
        if (viewData) {
            const missionId = viewData.missionid || null;
            setSelectedMissionId(missionId);
            if (missionId) {
                setDefaultMission({ id: missionId, date_mission: null, statut: null, type_mission: null, ville: null });
                setForm((prev) => ({
                    ...prev,
                    reference_dossier: `Mission #${missionId}`,
                }));
            } else {
                setDefaultMission(null);
                setForm((prev) => ({ ...prev, reference_dossier: "" }));
            }
        }
    }, [viewData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleMissionSelect = (mission) => {
        if (mission) {
            setSelectedMissionId(mission.id);
            setDefaultMission(mission);
            setForm((prev) => ({ ...prev, reference_dossier: `Mission #${mission.id}` }));
        } else {
            setSelectedMissionId(null);
            setDefaultMission(null);
            setForm((prev) => ({ ...prev, reference_dossier: "" }));
        }
    };

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files || []);
        const combined = [...fichiers, ...selected.map((f) => ({
            file    : f,
            name    : f.name,
            size    : f.size,
            tooLarge: f.size > MAX_FILE_SIZE,
        }))].slice(0, MAX_FILES);
        setFichiers(combined);
        e.target.value = "";
    };

    const removeFile = (idx) => setFichiers((prev) => prev.filter((_, i) => i !== idx));

    const resetForm = () => {
        setForm({ type_reclamation: "", niveau_gravite: "", service_concerne: "", description: "", reference_dossier: "" });
        setFichiers([]);
        setSelectedMissionId(null);
        setDefaultMission(null);
    };

    const handleClose = () => {
        if (!isSubmitting) { resetForm(); onClose(); }
    };

    const validate = () => {
        if (!form.type_reclamation) { toast({ title: "Type de réclamation requis", status: "error", duration: 3000, isClosable: true }); return false; }
        if (!form.niveau_gravite)   { toast({ title: "Niveau de gravité requis",   status: "error", duration: 3000, isClosable: true }); return false; }
        if (!form.service_concerne) { toast({ title: "Service concerné requis",    status: "error", duration: 3000, isClosable: true }); return false; }
        if (!form.description.trim()) { toast({ title: "Description requise",      status: "error", duration: 3000, isClosable: true }); return false; }
        if (fichiers.some((f) => f.tooLarge)) { toast({ title: "Un fichier dépasse 5 Mo", status: "error", duration: 3000, isClosable: true }); return false; }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setIsSubmitting(true);
        try {
            const pieces_jointes = await Promise.all(
                fichiers.map(async (f) => ({
                    nom_fichier   : f.name,
                    type_mime     : f.file.type || "application/octet-stream",
                    donnees_base64: await fileToBase64(f.file),
                }))
            );

            const payload = {
                ...form,
                avis_id   : viewData?.id       || null,
                mission_id: selectedMissionId  || viewData?.missionid || null,
                pieces_jointes,
            };

            const res = await axios.post(`${BASE_URL}/reclamations`, payload, { withCredentials: true });

            toast({
                title      : "Réclamation créée",
                description: `Numéro : ${res.data.numero_reclamation}`,
                status     : "success",
                duration   : 4000,
                isClosable : true,
            });

            resetForm();
            onCreated && onCreated(res.data);
            onClose();
        } catch (error) {
            toast({
                title      : "Erreur",
                description: error.response?.data?.error || "Une erreur est survenue lors de l'enregistrement.",
                status     : "error",
                duration   : 5000,
                isClosable : true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const clientName = patientData
        ? `${patientData.nom || ""} ${patientData.prenom || ""}`.trim()
        : "Client inconnu";

    return (
        <Modal isOpen={isOpen} onClose={handleClose} scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent
                boxShadow="2xl"
                maxH="100vh"
                h="100vh"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                position="fixed"
                right={0}
                top={0}
                bottom={0}
                m={0}
                maxW="780px"
                w="780px"
                borderRadius="0"
                borderTopLeftRadius="12px"
                borderBottomLeftRadius="12px"
            >
                <ModalHeader fontSize="xl" fontWeight="bold" py={4} px={6} borderBottom="1px solid" borderColor="gray.200" bg="white" borderTopLeftRadius="12px">
                    Traitement de la réclamation
                </ModalHeader>
                <ModalCloseButton top={4} right={4} borderRadius="full" _hover={{ bg: "gray.100" }} isDisabled={isSubmitting} />

                <ModalBody p={6} flex={1} overflowY="auto">
                    {loadingDetails ? (
                        <LoadingSpinner message="Chargement des détails..." size="md" />
                    ) : viewData ? (
                        <VStack spacing={6} align="stretch">

                            {/* ── Résumé de l'avis ─────────────────────────────── */}
                            <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="12px" p={4}>
                                <Text fontSize="xs" fontWeight="bold" color="blue.600" mb={3} textTransform="uppercase" letterSpacing="wide">
                                    Avis source
                                </Text>
                                <HStack spacing={3} align="start">
                                    <Avatar size="md" name={clientName} borderRadius="8px" />
                                    <VStack align="start" spacing={1} flex={1}>
                                        <Text fontWeight="bold" fontSize="sm" color="gray.900">{clientName}</Text>
                                        <HStack spacing={2} flexWrap="wrap">
                                            <HStack bg="white" px={2} py={0.5} borderRadius="6px" border="1px solid" borderColor="gray.200" spacing={1}>
                                                <Icon as={MdCalendarToday} color="gray.400" boxSize="12px" />
                                                <Text fontSize="xs" color="gray.600">
                                                    {viewData.créé_le ? new Date(viewData.créé_le).toLocaleDateString("fr-FR") : "—"}
                                                </Text>
                                            </HStack>
                                            {patientData?.téléphone && (
                                                <HStack bg="white" px={2} py={0.5} borderRadius="6px" border="1px solid" borderColor="gray.200" spacing={1}>
                                                    <Icon as={MdPhone} color="gray.400" boxSize="12px" />
                                                    <Text fontSize="xs" color="gray.600">{patientData.téléphone}</Text>
                                                </HStack>
                                            )}
                                            {patientData?.email && (
                                                <HStack bg="white" px={2} py={0.5} borderRadius="6px" border="1px solid" borderColor="gray.200" spacing={1}>
                                                    <Icon as={MdEmail} color="gray.400" boxSize="12px" />
                                                    <Text fontSize="xs" color="gray.600">{patientData.email}</Text>
                                                </HStack>
                                            )}
                                        </HStack>
                                        {/* Étoiles */}
                                        <HStack spacing={1} mt={1}>
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Image
                                                    key={star}
                                                    src={parseFloat(viewData.étoile) >= star ? starIcon : starEmptyIcon}
                                                    w="14px"
                                                    h="14px"
                                                />
                                            ))}
                                            <Text fontSize="xs" color="gray.600" ml={1}>({parseFloat(viewData.étoile).toFixed(1)})</Text>
                                        </HStack>
                                        {viewData.avi && (
                                            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="8px" p={2} mt={1} w="100%">
                                                <Text fontSize="xs" color="gray.700" fontStyle="italic">
                                                    "{viewData.avi}"
                                                </Text>
                                            </Box>
                                        )}
                                    </VStack>
                                </HStack>
                            </Box>

                            <Divider />

                            {/* ── Formulaire réclamation ────────────────────────── */}
                            <Text fontSize="sm" fontWeight="bold" color="gray.800">
                                Fiche de traitement des réclamations
                            </Text>

                            {/* Type + Gravité */}
                            <HStack spacing={4} align="start">
                                <FormControl isRequired flex={1}>
                                    <FormLabel fontSize="sm" fontWeight="semibold">Type de réclamation</FormLabel>
                                    <Select
                                        name="type_reclamation"
                                        value={form.type_reclamation}
                                        onChange={handleChange}
                                        placeholder="Sélectionner..."
                                    >
                                        {TYPES_RECLAMATION.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl isRequired flex={1}>
                                    <FormLabel fontSize="sm" fontWeight="semibold">Niveau de gravité</FormLabel>
                                    <Select
                                        name="niveau_gravite"
                                        value={form.niveau_gravite}
                                        onChange={handleChange}
                                        placeholder="Sélectionner..."
                                    >
                                        {NIVEAUX_GRAVITE.map((n) => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </Select>
                                    {form.niveau_gravite && (
                                        <Badge mt={1} colorScheme={graviteColor[form.niveau_gravite]} variant="subtle" borderRadius="full" px={2}>
                                            {form.niveau_gravite}
                                        </Badge>
                                    )}
                                </FormControl>
                            </HStack>

                            {/* Service concerné */}
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="semibold">Service concerné</FormLabel>
                                <Select
                                    name="service_concerne"
                                    value={form.service_concerne}
                                    onChange={handleChange}
                                    placeholder="Sélectionner..."
                                >
                                    {SERVICES.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Référence dossier — recherche mission */}
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="semibold">Référence dossier</FormLabel>
                                <MissionSearchInput
                                    value={selectedMissionId}
                                    defaultMission={defaultMission}
                                    onChange={handleMissionSelect}
                                    placeholder="Rechercher une mission..."
                                />
                            </FormControl>

                            {/* Description */}
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="semibold">Description</FormLabel>
                                <Textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Décrivez la réclamation en détail..."
                                    minH="120px"
                                    borderRadius="8px"
                                    maxLength={2000}
                                />
                                <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                                    {form.description.length}/2000
                                </Text>
                            </FormControl>

                            {/* Pièces jointes */}
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="semibold">
                                    Pièces jointes
                                    <Text as="span" fontSize="xs" color="gray.500" ml={2}>(max {MAX_FILES} fichiers, 5 Mo chacun)</Text>
                                </FormLabel>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    multiple
                                    onChange={handleFileChange}
                                />
                                {fichiers.length < MAX_FILES && (
                                    <Button
                                        leftIcon={<MdUploadFile />}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        borderRadius="8px"
                                        borderStyle="dashed"
                                        w="100%"
                                        py={6}
                                        color="gray.500"
                                        _hover={{ bg: "gray.50", borderColor: "blue.400", color: "blue.500" }}
                                    >
                                        Cliquer pour ajouter un fichier
                                    </Button>
                                )}
                                {fichiers.length > 0 && (
                                    <VStack align="stretch" mt={2} spacing={1}>
                                        {fichiers.map((f, idx) => (
                                            <HStack
                                                key={idx}
                                                bg={f.tooLarge ? "red.50" : "gray.50"}
                                                border="1px solid"
                                                borderColor={f.tooLarge ? "red.200" : "gray.200"}
                                                borderRadius="8px"
                                                px={3}
                                                py={2}
                                                justify="space-between"
                                            >
                                                <HStack spacing={2} flex={1} minW={0}>
                                                    {f.tooLarge && <Icon as={MdWarning} color="red.500" boxSize="14px" flexShrink={0} />}
                                                    <Text fontSize="xs" color={f.tooLarge ? "red.600" : "gray.700"} isTruncated>
                                                        {f.name}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                                                        ({(f.size / 1024).toFixed(0)} Ko)
                                                    </Text>
                                                </HStack>
                                                <Icon
                                                    as={MdClose}
                                                    cursor="pointer"
                                                    color="gray.400"
                                                    _hover={{ color: "red.500" }}
                                                    onClick={() => removeFile(idx)}
                                                />
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </FormControl>

                        </VStack>
                    ) : null}
                </ModalBody>

                <ModalFooter borderTop="1px solid" borderColor="gray.200" gap={3}>
                    <Button variant="outline" onClick={handleClose} isDisabled={isSubmitting} px={8}>
                        Annuler
                    </Button>
                    <Button
                        bg="#006133"
                        color="white"
                        _hover={{ bg: "#004d29" }}
                        px={8}
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        loadingText="Envoi..."
                    >
                        Créer la réclamation
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default TraitementReclamationModal;
